import qs from 'qs'
import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
    SLIDE_QUERY_PARAM,
    VIEWER_QUERY_PARAM,
} from '@Admin/AdminQuotingPage/constants'
import { ImDfmStateType } from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/constants'
import { imDfmFeedback_quoteModelRevision_dfmFeedback as ImDfmFeedbackType } from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/__generated__/imDfmFeedback'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'

import Box from '~/core/Box'
import { clickDfmCard } from '~/redux-modules/qmr-viewer/actions'
import { useExtractUrlSearchParams } from '~/lib/extractUrlSearchParams'
import { useFlagEnabled } from '~/lib/isFlagEnabled'
import { PROCESSID_IM } from '~/lib/partsConstants'

import ImDfmSlide from './ImDfmSlide'
import imDfmSlidesPanelQuery from './imDfmSlidesPanelQuery'
import { handleKeyDown } from './logic'
import styles from './styles.css'
import { imDfmSlidesPanel_quoteModelRevision_dfmFeedback as DfmFeedbackType } from './__generated__/imDfmSlidesPanel'

type imDfmSlidesPanelPropsType = {
    qmrId?: string
}
const ImDfmSlidesPanel = (props: imDfmSlidesPanelPropsType) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { search } = useLocation()
    const { qmrId: urlQmrId } = useParams<{ qmrId: string }>()
    let qmrId = urlQmrId

    if (!qmrId) {
        qmrId = props?.qmrId
    }
    const slideRef = useRef<HTMLDivElement>(null)

    const { data } = useQuery(imDfmSlidesPanelQuery, {
        variables: { id: qmrId },
        skip: !qmrId,
    })

    const dfmFeedback = data?.quoteModelRevision?.dfmFeedback
    const isNonImDfmEnabled = useFlagEnabled(PROVISIONED_UI_FLAGS.NON_IM_DFM_UI)
    const isHighTouch = data?.quoteModelRevision?.quoteModel?.quote?.isCpqOwned
    const isImQuoteType =
        data?.quoteModelRevision?.quoteModel?.quote?.type === PROCESSID_IM
    const setActiveSlideQueryParam = useCallback(
        (dfmFeedbackId: string) => {
            navigate(
                {
                    search: qs.stringify({
                        [SLIDE_QUERY_PARAM]: dfmFeedbackId,
                    }),
                },
                { replace: true },
            )
        },
        [navigate],
    )

    const { slide: activeSlideId, viewer: viewerType } =
        useExtractUrlSearchParams(search, [
            SLIDE_QUERY_PARAM,
            VIEWER_QUERY_PARAM,
        ])

    useEffect(() => {
        if (dfmFeedback) {
            const activeSlideIndex = dfmFeedback.findIndex(
                (dfm: ImDfmFeedbackType) => dfm?.id === activeSlideId,
            )
            const activeSlideNumber =
                activeSlideIndex < 0 ? 0 : activeSlideIndex
            const dfmFeedbackId = dfmFeedback[activeSlideNumber]?.id

            if (slideRef?.current && activeSlideNumber && dfmFeedbackId) {
                slideRef?.current?.children[activeSlideNumber].scrollIntoView(
                    false,
                )
            }

            slideRef?.current?.focus()
        }
    }, [activeSlideId, dfmFeedback, setActiveSlideQueryParam])

    useEffect(() => {
        // Update 3D viz in the ImDfm3dViewer
        if (activeSlideId) {
            const dfmSnapshot = dfmFeedback?.find(
                (dfm: DfmFeedbackType) => dfm.id === activeSlideId,
            )?.modelViewerSnapshots[0]?.snapshot

            dispatch(clickDfmCard({ dfmId: activeSlideId, dfmSnapshot }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSlideId, viewerType])

    return (
        <Box
            className={styles.imDfmSlide}
            direction="col"
            height="100%"
            ref={slideRef}
            onKeyDown={event => {
                handleKeyDown(
                    event,
                    dfmFeedback,
                    activeSlideId,
                    setActiveSlideQueryParam,
                )
            }}
            tabIndex={0}
            width="100%"
        >
            {dfmFeedback?.map((dfm: ImDfmFeedbackType, idx: number) => (
                <ImDfmSlide
                    dfmFeedbackId={dfm?.id}
                    idx={idx}
                    key={dfm?.id}
                    attachmentId={dfm?.attachment?.id}
                    sourceType={dfm?.sourceType}
                    status={dfm?.status as ImDfmStateType}
                    topic={dfm?.topic?.label}
                    title={dfm?.title || ''}
                    isNonImDfmEnabled={isNonImDfmEnabled}
                    isHighTouch={isHighTouch}
                    isImQuoteType={isImQuoteType}
                />
            ))}
        </Box>
    )
}

export default ImDfmSlidesPanel
